import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { CommonReduxState } from '@src/models/commons/reduxState.interface';
import type {
  IWorkJourneyManagementAttentionItem,
  IWorkJourneyManagementCalendarCell,
  IWorkJourneyManagementCalendarMonthSummary,
  IWorkJourneyManagementJourneyListItem,
  IWorkJourneyManagementJourneysMonthSummary,
  TWorkJourneyManagementTab,
} from '@src/models/interfaces/workJourneyManagement.interface';
import {
  get as getSessionStorage,
  set as setSessionStorage,
} from '@src/packages/utils/sessionStorage';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';
import WorkJourneyService from '@src/services/WorkJourneyService';

import {
  isWorkJourneyManagementTab,
  WORK_JOURNEY_ATTENTION_PAGE_SIZE,
  WORK_JOURNEY_MANAGEMENT_ACTIVE_TAB_STORAGE_KEY,
  WORK_JOURNEY_MANAGEMENT_DEFAULT_TAB,
  WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_ALL,
  WORK_JOURNEY_JOURNEYS_PAGE_SIZE,
} from '../constants';

export interface WorkJourneyManagementJourneyConfigOption {
  id: string;
  name: string;
}

export interface WorkJourneyManagementContextValue {
  orgId: string;
  activeTab: TWorkJourneyManagementTab;
  setActiveTab: (tab: TWorkJourneyManagementTab) => void;
  journeyFilter: string;
  setJourneyFilter: (filter: string) => void;
  journeyConfigs: WorkJourneyManagementJourneyConfigOption[];
  attentionItems: IWorkJourneyManagementAttentionItem[];
  attentionTotal: number;
  attentionPage: number;
  attentionPageSize: number;
  setAttentionPage: (page: number) => void;
  journeysSummary: IWorkJourneyManagementJourneysMonthSummary | null;
  journeyItems: IWorkJourneyManagementJourneyListItem[];
  journeysTotal: number;
  journeysPage: number;
  journeysPageSize: number;
  setJourneysPage: (page: number) => void;
  calendarSummary: IWorkJourneyManagementCalendarMonthSummary | null;
  calendarCells: IWorkJourneyManagementCalendarCell[];
  /** Shared visible month for journeys + calendar (1–12). */
  selectedMonth: number;
  selectedYear: number;
  /** @deprecated Alias of selectedMonth — kept for existing calendar consumers. */
  calendarMonth: number;
  /** @deprecated Alias of selectedYear — kept for existing calendar consumers. */
  calendarYear: number;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToPreviousCalendarMonth: () => void;
  goToNextCalendarMonth: () => void;
  /** Jump journeys + calendar to an absolute month/year (panel MonthPicker). */
  jumpToMonthYear: (month: number, year: number) => void;
  loading: boolean;
  journeysLoading: boolean;
  calendarLoading: boolean;
  error: string | null;
  journeysError: string | null;
  calendarError: string | null;
  /**
   * Reloads attention rows. Pass `paginate: false` to fetch the full list
   * (secondary client-side filters). Defaults to paginated on the attention tab.
   * Pass `page` for server pagination so callers can avoid tying this callback
   * identity to `attentionPage` (prevents redundant refetches).
   */
  refreshAttentionItems: (options?: { paginate?: boolean; page?: number }) => Promise<void>;
  /**
   * Reloads journeys. Pass `paginate: false` to fetch the full month list
   * (shift filter options / secondary filters). Defaults to paginated on the
   * journeys tab and full list on other tabs.
   * Pass `page` for server pagination (same reason as attention).
   * Pass `userId` only when filtering by plantilla membership (server-side).
   */
  refreshJourneys: (options?: {
    paginate?: boolean;
    page?: number;
    userId?: string;
  }) => Promise<void>;
  refreshCalendar: (options?: { userId?: string }) => Promise<void>;
  /** Plantilla user currently applied to the calendar (and day report). */
  calendarUserId?: string;
  /** Reloads whichever tab is visible (attention, journeys or calendar). */
  refreshActiveTab: () => Promise<void>;
}

interface WorkJourneyManagementProviderProps {
  children: ReactNode;
}

export const WorkJourneyManagementContext = createContext<WorkJourneyManagementContextValue | null>(
  null,
);
const service = new WorkJourneyManagementService();
const workJourneyService = new WorkJourneyService();

const getCurrentMonthYear = () => {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
};

const shiftMonthYear = (month: number, year: number, delta: number) => {
  const date = new Date(year, month - 1 + delta, 1);
  return { month: date.getMonth() + 1, year: date.getFullYear() };
};

const readStoredActiveTab = (): TWorkJourneyManagementTab => {
  const stored = getSessionStorage(WORK_JOURNEY_MANAGEMENT_ACTIVE_TAB_STORAGE_KEY);
  return isWorkJourneyManagementTab(stored) ? stored : WORK_JOURNEY_MANAGEMENT_DEFAULT_TAB;
};

export const WorkJourneyManagementProvider = ({ children }: WorkJourneyManagementProviderProps) => {
  const intl = useIntl();
  const orgId = useSelector(({ organizations }: CommonReduxState) => organizations.organization.id);

  const [activeTab, setActiveTabState] = useState<TWorkJourneyManagementTab>(readStoredActiveTab);
  const setActiveTab = useCallback((tab: TWorkJourneyManagementTab) => {
    setActiveTabState(tab);
    setSessionStorage(WORK_JOURNEY_MANAGEMENT_ACTIVE_TAB_STORAGE_KEY, tab);
  }, []);
  const [journeyFilter, setJourneyFilter] = useState(WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_ALL);
  const [journeyConfigs, setJourneyConfigs] = useState<WorkJourneyManagementJourneyConfigOption[]>(
    [],
  );
  const [attentionItems, setAttentionItems] = useState<IWorkJourneyManagementAttentionItem[]>([]);
  const [attentionTotal, setAttentionTotal] = useState(0);
  const [attentionPage, setAttentionPage] = useState(1);
  const [attentionPageSize] = useState(WORK_JOURNEY_ATTENTION_PAGE_SIZE);
  const [journeysSummary, setJourneysSummary] =
    useState<IWorkJourneyManagementJourneysMonthSummary | null>(null);
  const [journeyItems, setJourneyItems] = useState<IWorkJourneyManagementJourneyListItem[]>([]);
  const [journeysTotal, setJourneysTotal] = useState(0);
  const [journeysPage, setJourneysPage] = useState(1);
  const [journeysPageSize] = useState(WORK_JOURNEY_JOURNEYS_PAGE_SIZE);
  const [calendarSummary, setCalendarSummary] =
    useState<IWorkJourneyManagementCalendarMonthSummary | null>(null);
  const [calendarCells, setCalendarCells] = useState<IWorkJourneyManagementCalendarCell[]>([]);
  const [{ month: selectedMonth, year: selectedYear }, setMonthYear] =
    useState(getCurrentMonthYear);
  const [loading, setLoading] = useState(false);
  const [journeysLoading, setJourneysLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [journeysError, setJourneysError] = useState<string | null>(null);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const attentionRequestIdRef = useRef(0);
  const journeysRequestIdRef = useRef(0);
  const calendarRequestIdRef = useRef(0);

  useEffect(() => {
    if (!orgId) {
      setJourneyConfigs([]);
      return;
    }

    let cancelled = false;

    const loadJourneyConfigs = async () => {
      try {
        const configs = await workJourneyService.listConfigs(orgId);
        if (!cancelled) {
          setJourneyConfigs(configs);
        }
      } catch {
        if (!cancelled) {
          setJourneyConfigs([]);
        }
      }
    };

    loadJourneyConfigs();

    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const attentionPageRef = useRef(attentionPage);
  attentionPageRef.current = attentionPage;
  const journeysPageRef = useRef(journeysPage);
  journeysPageRef.current = journeysPage;
  /** Last listJourneys / listCalendar userId so refreshActiveTab keeps the filter. */
  const journeysListUserIdRef = useRef<string | undefined>(undefined);
  const calendarListUserIdRef = useRef<string | undefined>(undefined);
  const [calendarUserId, setCalendarUserId] = useState<string | undefined>(undefined);

  const refreshAttentionItems = useCallback(
    async (options?: { paginate?: boolean; page?: number }) => {
      if (!orgId) return;
      const requestId = ++attentionRequestIdRef.current;
      setLoading(true);
      setError(null);
      const paginate = options?.paginate ?? activeTab === 'requires_attention';
      const page = options?.page ?? attentionPageRef.current;
      try {
        const result = await service.listAttentionItems({
          organizationId: orgId,
          workJourneyConfigId:
            journeyFilter === WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_ALL
              ? undefined
              : journeyFilter,
          ...(paginate
            ? {
                from: (page - 1) * attentionPageSize,
                pageSize: attentionPageSize,
              }
            : {}),
        });
        if (requestId !== attentionRequestIdRef.current) return;
        setAttentionItems(result.items);
        setAttentionTotal(result.total);
      } catch {
        if (requestId !== attentionRequestIdRef.current) return;
        setAttentionItems([]);
        setAttentionTotal(0);
        setError(intl.formatMessage({ id: 'general.save.error.message' }));
      } finally {
        if (requestId === attentionRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [intl, orgId, journeyFilter, activeTab, attentionPageSize],
  );

  const refreshJourneys = useCallback(
    async (options?: { paginate?: boolean; page?: number; userId?: string }) => {
      if (!orgId) return;
      const requestId = ++journeysRequestIdRef.current;
      setJourneysLoading(true);
      setJourneysError(null);
      const paginate = options?.paginate ?? activeTab === 'journeys';
      const page = options?.page ?? journeysPageRef.current;
      if (options) {
        journeysListUserIdRef.current = options.userId;
      }
      const userId = options ? options.userId : journeysListUserIdRef.current;
      try {
        const result = await service.listJourneys({
          organizationId: orgId,
          month: selectedMonth,
          year: selectedYear,
          workJourneyConfigId:
            journeyFilter === WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_ALL
              ? undefined
              : journeyFilter,
          ...(userId ? { userId } : {}),
          ...(paginate
            ? {
                from: (page - 1) * journeysPageSize,
                pageSize: journeysPageSize,
              }
            : {}),
        });
        if (requestId !== journeysRequestIdRef.current) return;
        setJourneysSummary(result.summary);
        setJourneyItems(result.items);
        setJourneysTotal(result.total);
      } catch {
        if (requestId !== journeysRequestIdRef.current) return;
        setJourneysSummary(null);
        setJourneyItems([]);
        setJourneysTotal(0);
        setJourneysError(intl.formatMessage({ id: 'general.save.error.message' }));
      } finally {
        if (requestId === journeysRequestIdRef.current) {
          setJourneysLoading(false);
        }
      }
    },
    [intl, orgId, selectedMonth, selectedYear, journeyFilter, activeTab, journeysPageSize],
  );

  const refreshCalendar = useCallback(
    async (options?: { userId?: string }) => {
      if (!orgId) return;
      const requestId = ++calendarRequestIdRef.current;
      setCalendarLoading(true);
      setCalendarError(null);
      if (options) {
        calendarListUserIdRef.current = options.userId;
      }
      const userId = options ? options.userId : calendarListUserIdRef.current;
      setCalendarUserId(userId);
      try {
        const result = await service.listCalendar({
          organizationId: orgId,
          month: selectedMonth,
          year: selectedYear,
          workJourneyConfigId:
            journeyFilter === WORK_JOURNEY_MANAGEMENT_JOURNEY_FILTER_ALL
              ? undefined
              : journeyFilter,
          ...(userId ? { userId } : {}),
        });
        if (requestId !== calendarRequestIdRef.current) return;
        setCalendarSummary(result.summary);
        setCalendarCells(result.cells);
      } catch {
        if (requestId !== calendarRequestIdRef.current) return;
        setCalendarSummary(null);
        setCalendarCells([]);
        setCalendarError(intl.formatMessage({ id: 'general.save.error.message' }));
      } finally {
        if (requestId === calendarRequestIdRef.current) {
          setCalendarLoading(false);
        }
      }
    },
    [intl, orgId, selectedMonth, selectedYear, journeyFilter],
  );

  /** Reloads the visible tab — used after cover / resolve / absence actions. */
  const refreshActiveTab = useCallback(async () => {
    if (activeTab === 'requires_attention') return refreshAttentionItems();
    if (activeTab === 'journeys') return refreshJourneys();
    return refreshCalendar();
  }, [activeTab, refreshAttentionItems, refreshCalendar, refreshJourneys]);

  const jumpToMonthYear = useCallback((month: number, year: number) => {
    setJourneysPage(1);
    setAttentionPage(1);
    setMonthYear({ month, year });
  }, []);

  const goToPreviousMonth = useCallback(() => {
    setJourneysPage(1);
    setMonthYear(({ month, year }) => shiftMonthYear(month, year, -1));
  }, []);

  const goToNextMonth = useCallback(() => {
    setJourneysPage(1);
    setMonthYear(({ month, year }) => shiftMonthYear(month, year, 1));
  }, []);

  const value = useMemo(
    () => ({
      orgId,
      activeTab,
      setActiveTab,
      journeyFilter,
      setJourneyFilter: (filter: string) => {
        setJourneysPage(1);
        setAttentionPage(1);
        setJourneyFilter(filter);
      },
      journeyConfigs,
      attentionItems,
      attentionTotal,
      attentionPage,
      attentionPageSize,
      setAttentionPage,
      journeysSummary,
      journeyItems,
      journeysTotal,
      journeysPage,
      journeysPageSize,
      setJourneysPage,
      calendarSummary,
      calendarCells,
      selectedMonth,
      selectedYear,
      calendarMonth: selectedMonth,
      calendarYear: selectedYear,
      goToPreviousMonth,
      goToNextMonth,
      goToPreviousCalendarMonth: goToPreviousMonth,
      goToNextCalendarMonth: goToNextMonth,
      jumpToMonthYear,
      loading,
      journeysLoading,
      calendarLoading,
      error,
      journeysError,
      calendarError,
      refreshAttentionItems,
      refreshJourneys,
      refreshCalendar,
      calendarUserId,
      refreshActiveTab,
    }),
    [
      orgId,
      activeTab,
      setActiveTab,
      journeyFilter,
      journeyConfigs,
      attentionItems,
      attentionTotal,
      attentionPage,
      attentionPageSize,
      journeysSummary,
      journeyItems,
      journeysTotal,
      journeysPage,
      journeysPageSize,
      calendarSummary,
      calendarCells,
      selectedMonth,
      selectedYear,
      goToPreviousMonth,
      goToNextMonth,
      jumpToMonthYear,
      loading,
      journeysLoading,
      calendarLoading,
      error,
      journeysError,
      calendarError,
      refreshAttentionItems,
      refreshJourneys,
      refreshCalendar,
      calendarUserId,
      refreshActiveTab,
    ],
  );

  return (
    <WorkJourneyManagementContext.Provider value={value}>
      {children}
    </WorkJourneyManagementContext.Provider>
  );
};

export const useWorkJourneyManagementContext = (): WorkJourneyManagementContextValue => {
  const context = useContext(WorkJourneyManagementContext);

  if (!context) {
    throw new Error(
      'useWorkJourneyManagementContext must be used within WorkJourneyManagementProvider',
    );
  }

  return context;
};
